import { Maybe } from 'types/Maybe'

const TOP_OFFSET = 32
const FOLLOW_THRESHOLD = 70

interface ChatViewportMetrics {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
  lastTurnHeight?: number
}

interface ChatViewportState {
  isAnchored: boolean
  isFollowing: boolean
  isPausedByUser: boolean
  previousChatId?: string
  openedChatId: Maybe<string>
  lastScrollTop: number
}

interface ChatViewportIntent {
  gutterHeight?: number
  scroll?: { to: 'bottom' | 'anchor'; behavior: 'auto' | 'smooth' }
  showScrollToBottom?: boolean
  remeasureScrollToBottom?: boolean
}

type ChatViewportSignal =
  | { type: 'attached'; metrics: ChatViewportMetrics }
  | { type: 'chatChanged'; chatId?: string; hasAnchor: boolean }
  | { type: 'chatLoaded'; chatId: string }
  | { type: 'messageSent'; metrics: ChatViewportMetrics }
  | { type: 'contentResized'; metrics: ChatViewportMetrics }
  | { type: 'scrolled'; metrics: ChatViewportMetrics }
  | { type: 'scrollToBottomRequested' }

interface ChatViewportDecision {
  state: ChatViewportState
  intent: ChatViewportIntent
}

const createChatViewportState = (chatId?: string): ChatViewportState => ({
  isAnchored: false,
  isFollowing: false,
  isPausedByUser: false,
  previousChatId: chatId,
  openedChatId: null,
  lastScrollTop: 0,
})

const getGutterHeight = (
  { isAnchored }: ChatViewportState,
  { clientHeight, lastTurnHeight }: ChatViewportMetrics,
) => (isAnchored && lastTurnHeight !== undefined
  ? Math.max(0, clientHeight - lastTurnHeight - TOP_OFFSET)
  : 0)

const isScrollToBottomVisible = (state: ChatViewportState, distance: number) => (
  state.isPausedByUser && distance > FOLLOW_THRESHOLD
)

const onScrolled = (
  state: ChatViewportState,
  { scrollTop, scrollHeight, clientHeight }: ChatViewportMetrics,
): ChatViewportDecision => {
  const distance = scrollHeight - clientHeight - scrollTop
  let { isFollowing, isPausedByUser } = state
  if (scrollTop < state.lastScrollTop && distance > 1) {
    isFollowing = false
    isPausedByUser = true
  } else if (distance <= FOLLOW_THRESHOLD) {
    isFollowing = true
    isPausedByUser = false
  }
  const next = { ...state, isFollowing, isPausedByUser, lastScrollTop: scrollTop }

  return { state: next, intent: { showScrollToBottom: isScrollToBottomVisible(next, distance) } }
}

const decideChatViewport = (
  state: ChatViewportState,
  signal: ChatViewportSignal,
): ChatViewportDecision => {
  switch (signal.type) {
    case 'attached':
      return { state: { ...state, lastScrollTop: signal.metrics.scrollTop }, intent: {} }
    case 'chatChanged': {
      if (signal.chatId === state.previousChatId) return { state, intent: {} }
      const changed = {
        ...state,
        previousChatId: signal.chatId,
        openedChatId: signal.hasAnchor && signal.chatId ? signal.chatId : null,
      }
      if (signal.hasAnchor) return { state: changed, intent: {} }

      return {
        state: { ...changed, isAnchored: false, isFollowing: false, isPausedByUser: false },
        intent: { showScrollToBottom: false },
      }
    }
    case 'chatLoaded':
      if (state.openedChatId === signal.chatId) return { state, intent: {} }

      return {
        state: { ...state, openedChatId: signal.chatId, isFollowing: true },
        intent: { scroll: { to: 'bottom', behavior: 'auto' } },
      }
    case 'messageSent': {
      const anchored = { ...state, isAnchored: true, isFollowing: false, isPausedByUser: false }

      return {
        state: anchored,
        intent: {
          gutterHeight: getGutterHeight(anchored, signal.metrics),
          scroll: { to: 'anchor', behavior: 'smooth' },
          showScrollToBottom: false,
        },
      }
    }
    case 'contentResized':
      return {
        state,
        intent: {
          gutterHeight: getGutterHeight(state, signal.metrics),
          ...(state.isFollowing && { scroll: { to: 'bottom', behavior: 'auto' } }),
          remeasureScrollToBottom: true,
        },
      }
    case 'scrolled':
      return onScrolled(state, signal.metrics)
    case 'scrollToBottomRequested':
      return {
        state: { ...state, isFollowing: true, isPausedByUser: false },
        intent: { scroll: { to: 'bottom', behavior: 'smooth' }, showScrollToBottom: false },
      }
    default:
      return { state, intent: {} }
  }
}

export type {
  ChatViewportMetrics,
  ChatViewportState,
  ChatViewportIntent,
  ChatViewportSignal,
}
export {
  TOP_OFFSET,
  FOLLOW_THRESHOLD,
  createChatViewportState,
  decideChatViewport,
  isScrollToBottomVisible,
}
